import { Link } from 'react-router-dom';
import { Rss } from 'lucide-react';
import { TOOLS } from '../lib/tools';

/**
 * Shared marketing-site footer (landing, features, tools, alternatives…).
 *
 * Multi-column layout. The "Herramientas gratuitas" column is driven by the
 * `TOOLS` catalog so it stays in sync automatically; the alternatives list is
 * a small static mirror of `server/src/seo/alternativesData.ts` (keep in sync
 * if competitors are added/removed).
 */

/** Mirror of the slugs in server/src/seo/alternativesData.ts. */
const ALTERNATIVES: { slug: string; competitor: string }[] = [
  { slug: 'metricool', competitor: 'Metricool' },
  { slug: 'buffer', competitor: 'Buffer' },
  { slug: 'hootsuite', competitor: 'Hootsuite' },
  { slug: 'later', competitor: 'Later' },
  { slug: 'publer', competitor: 'Publer' },
  { slug: 'socialbee', competitor: 'SocialBee' },
];

/** Short labels for the tools column (the catalog titles are long). */
const TOOL_LABELS: Record<string, string> = {
  'contador-de-caracteres': 'Contador de caracteres',
  'mejor-hora-para-publicar': 'Mejor hora para publicar',
  'calculadora-de-engagement': 'Calculadora de engagement',
  'generador-de-bio': 'Generador de bio',
  'generador-de-hashtags': 'Generador de hashtags',
  'generador-de-captions': 'Generador de captions',
  'generador-de-ideas': 'Generador de ideas',
};

const linkClass = 'block text-sm text-slate-500 hover:text-slate-900 transition';

function Column({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <div className="mt-4 space-y-2.5">{children}</div>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid gap-10 lg:gap-8 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-7 w-auto" />
            <p className="mt-4 text-sm text-slate-500 max-w-[14rem] leading-relaxed">
              Programa y publica en todas tus redes con IA.
            </p>
          </div>

          <Column title="Producto">
            <a href="/#como-funciona" className={linkClass}>Cómo funciona</a>
            <Link to="/features" className={linkClass}>Características</Link>
            <Link to="/precios" className={linkClass}>Precios</Link>
            <a href="/#faqs" className={linkClass}>FAQs</a>
            <Link to="/blog" className={linkClass}>Blog</Link>
            <Link to="/login" className={linkClass}>Iniciar sesión</Link>
          </Column>

          <Column title="Alternativas">
            {ALTERNATIVES.map((a) => (
              <Link key={a.slug} to={`/alternativas/${a.slug}`} className={linkClass}>
                Zettapost vs {a.competitor}
              </Link>
            ))}
            <Link to="/alternativas" className={`${linkClass} font-medium text-slate-600`}>
              Todas las alternativas →
            </Link>
          </Column>

          <Column title="Herramientas gratuitas">
            {TOOLS.map((t) => (
              <Link key={t.slug} to={`/herramientas-gratuitas/${t.slug}`} className={linkClass}>
                {TOOL_LABELS[t.slug] ?? t.title}
              </Link>
            ))}
            <Link to="/herramientas-gratuitas" className={`${linkClass} font-medium text-slate-600`}>
              Todas las herramientas →
            </Link>
          </Column>

          <Column title="Integraciones">
            <Link to="/chatgpt" className={linkClass}>ChatGPT</Link>
            <Link to="/integraciones/claude" className={linkClass}>Claude</Link>
            <Link to="/integraciones/cursor" className={linkClass}>Cursor</Link>
            <Link to="/integraciones/gemini" className={linkClass}>Gemini</Link>
            <Link to="/integraciones" className={`${linkClass} font-medium text-slate-600`}>
              Todas las integraciones →
            </Link>
          </Column>
        </div>

        {/* Divider + bottom bar */}
        <div className="mt-12 border-t border-slate-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <span>© {new Date().getFullYear()} Zettapost · El programador de redes sociales con IA.</span>
          <div className="flex items-center gap-5">
            <a
              href="/blog/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-slate-700"
            >
              <Rss size={14} />
              RSS
            </a>
            <Link to="/terms" className="hover:text-slate-700">Términos</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacidad</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
